<template>
  <div class="ghl-contact-card">
    <div
      v-if="loading"
      class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>

    <div
      v-else-if="!leadContact"
      class="text-center py-8">
      <p class="text-gray-500 mb-4">
        No lead contact assigned
      </p>
      <button
        class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium
               rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none
               focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        @click="openContactSelector">
        <svg
          class="mr-2 -ml-0.5 h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4" />
        </svg>
        Assign Lead Contact
      </button>
    </div>

    <div
      v-else
      class="space-y-4">
      <div class="flex justify-between items-start">
        <div>
          <h4 class="text-lg font-medium text-gray-900">
            {{ leadContact.display_name }}
          </h4>
          <p
            v-if="leadContact.email"
            class="text-sm text-gray-500">
            {{ leadContact.email }}
          </p>
          <p
            v-if="leadContact.phone"
            class="text-sm text-gray-500">
            {{ leadContact.phone }}
          </p>
          <p
            v-if="leadContact.company_name"
            class="text-sm text-gray-500">
            {{ leadContact.company_name }}
          </p>
        </div>
        <button
          class="text-sm text-indigo-600 hover:text-indigo-500"
          @click="openContactSelector">
          Change
        </button>
      </div>

      <div
        v-if="leadContact.tags && leadContact.tags.length > 0"
        class="flex flex-wrap gap-2">
        <span
          v-for="tag in leadContact.tags"
          :key="tag"
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
          {{ tag }}
        </span>
      </div>

      <div class="border-t pt-4">
        <h5 class="text-sm font-medium text-gray-900 mb-2">
          Recent Activity
        </h5>
        <div
          v-if="recentConversations.length > 0"
          class="space-y-2">
          <div
            v-for="conversation in recentConversations"
            :key="conversation.id"
            class="text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">{{ conversation.type }}</span>
              <span class="text-gray-500">{{ formatDate(conversation.last_message_at) }}</span>
            </div>
            <p
              v-if="conversation.unread_count > 0"
              class="text-xs text-red-600">
              {{ conversation.unread_count }} unread messages
            </p>
          </div>
        </div>
        <p
          v-else
          class="text-sm text-gray-500">
          No recent conversations
        </p>
      </div>

      <div
        v-if="leadContact.last_activity_at"
        class="text-xs text-gray-500">
        Last activity: {{ formatDate(leadContact.last_activity_at) }}
      </div>

      <!-- Attribution Data Section -->
      <div
        v-if="hasAttributionData"
        class="border-t pt-4 mt-4">
        <h5 class="text-sm font-medium text-gray-900 mb-2">
          Attribution Data
        </h5>
        <div class="space-y-2">
          <div
            v-if="leadContact.attribution?.source"
            class="text-sm">
            <span class="text-gray-600">Source:</span>
            <span class="ml-2 font-medium text-gray-900">{{ leadContact.attribution.source }}</span>
          </div>
          <div
            v-if="leadContact.attribution?.medium"
            class="text-sm">
            <span class="text-gray-600">Medium:</span>
            <span class="ml-2 font-medium text-gray-900">{{ leadContact.attribution.medium }}</span>
          </div>
          <div
            v-if="leadContact.attribution?.session_source"
            class="text-sm">
            <span class="text-gray-600">Session Source:</span>
            <span class="ml-2 font-medium text-gray-900">{{ leadContact.attribution.session_source }}</span>
          </div>
          <!-- UTM fields if present -->
          <div
            v-if="leadContact.attribution?.utm_content"
            class="text-sm">
            <span class="text-gray-600">UTM Content:</span>
            <span class="ml-2 font-medium text-gray-900">{{ leadContact.attribution.utm_content }}</span>
          </div>
          <div
            v-if="leadContact.attribution?.utm_medium"
            class="text-sm">
            <span class="text-gray-600">UTM Medium:</span>
            <span class="ml-2 font-medium text-gray-900">{{ leadContact.attribution.utm_medium }}</span>
          </div>
          <div
            v-if="leadContact.attribution?.utm_campaign"
            class="text-sm">
            <span class="text-gray-600">UTM Campaign:</span>
            <span class="ml-2 font-medium text-gray-900">{{ leadContact.attribution.utm_campaign }}</span>
          </div>
          <div
            v-if="leadContact.attribution?.utm_source"
            class="text-sm">
            <span class="text-gray-600">UTM Source:</span>
            <span class="ml-2 font-medium text-gray-900">{{ leadContact.attribution.utm_source }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Selector Modal -->
    <ContactSelectorModal
      v-if="showSelector"
      :organization-id="organizationId"
      instance-type="lead"
      @select="assignContact"
      @close="showSelector = false" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
// Date formatting utility
import ContactSelectorModal from './contact_selector_modal.vue'
import useGhlCache from '@/vue_widgets/composables/use_ghl_cache'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

const loading = ref(true)
const leadContact = ref(null)
const recentConversations = ref([])
const showSelector = ref(false)
const { cachedFetch } = useGhlCache()

const hasAttributionData = computed(() => {
  if (!leadContact.value?.attribution) return false

  const attr = leadContact.value.attribution
  return (
    attr.source ||
    attr.medium ||
    attr.session_source ||
    attr.utm_content ||
    attr.utm_medium ||
    attr.utm_campaign ||
    attr.utm_source ||
    attr.utm_term
  )
})

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

const openContactSelector = () => {
  showSelector.value = true
}

const fetchConversations = async(contactId) => {
  try {
    const response = await cachedFetch(`/ajax/ghl/contacts/${contactId}/conversations`)
    const data = await response.json()
    recentConversations.value = data.conversations || []
  } catch (error) {
    // Failed to fetch conversations
  }
}

const assignContact = async(contact) => {
  try {
    const response = await fetch(`/ajax/ghl/contacts/${contact.id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        assign_as: 'lead_contact',
        organization_id: parseInt(props.organizationId, 10)
      })
    })

    if (response.ok) {
      leadContact.value = contact
      await fetchConversations(contact.id)
    }
  } catch (error) {
    // Failed to assign contact
  } finally {
    showSelector.value = false
  }
}

const fetchLeadContact = async() => {
  try {
    loading.value = true
    const response = await cachedFetch(`/ajax/organizations/${parseInt(props.organizationId, 10)}`)
    const data = await response.json()

    if (data.lead_contact) {
      leadContact.value = data.lead_contact
      await fetchConversations(data.lead_contact.id)
    }
  } catch (error) {
    // Failed to fetch lead contact
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchLeadContact()
})
</script>

<style scoped>
.ghl-contact-card {
  @apply bg-white rounded-lg p-6 shadow-sm;
}
</style>
